import { PanelLeftCloseIcon, PanelLeftOpenIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { DraftStepType } from '../builder/types';
import { PALETTE_ITEMS } from './paletteItems';

/**
 * The MIME-ish key the palette writes and the canvas reads.
 *
 * A custom type rather than `text/plain`, so a drag from anywhere else — a text
 * selection, a file — is not mistaken for a palette drop.
 */
export const PALETTE_DRAG_TYPE = 'application/x-workflow-step';

/**
 * The three things that can be added to a canvas.
 *
 * Native HTML5 drag rather than dnd-kit (research D7): the canvas already owns
 * pointer events for panning and connecting, and a second drag system competing
 * for them is how a palette drop starts a pan instead. It would also pull
 * dnd-kit into this chunk, which the bundle gate measures.
 *
 * Each item is also a button. Dragging is the discoverable gesture, but it is
 * not the only way in: a keyboard user, or anyone who would rather click than
 * drag, gets the same result from `onAdd` (US8).
 *
 * **Collapsing keeps both of those.** A rail of icons is still draggable and
 * still clickable — the panel gives up its labels, not its function, so folding
 * it away to see more canvas never costs a user the ability to add a step.
 */
export function ComponentPalette({
  onAdd,
  collapsed,
  onToggleCollapsed,
}: {
  onAdd: (type: DraftStepType) => void;
  collapsed: boolean;
  onToggleCollapsed: () => void;
}) {
  return (
    <div
      className={cn(
        'flex flex-col',
        // Collapsed, this is a *rail*: the items sit close enough to read as one
        // strip of controls. At the expanded spacing they float as three
        // unrelated boxes, which is both uglier and harder to aim at.
        collapsed ? 'w-7 gap-1' : 'w-52 gap-2',
      )}
      data-testid="component-palette"
      data-collapsed={collapsed}
    >
      {/*
        The toggle is anchored left in both states, deliberately. Put it on the
        far edge when expanded and it has to jump the width of the panel the
        moment the panel collapses — a pop no width transition can smooth over,
        because it is the content moving rather than the container.
      */}
      <div className="flex items-center gap-1.5">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onToggleCollapsed}
          aria-label={
            collapsed ? 'Expand the step palette' : 'Collapse the step palette'
          }
          aria-expanded={!collapsed}
          aria-controls="component-palette-items"
          // An *inset* ring, and the button's own outward one cancelled. The
          // rail is exactly as wide as this control, and its container clips
          // horizontally — that clipping is what keeps the collapse animation
          // from reflowing the labels — so a ring drawn outside the box is a
          // focus indicator nobody can see. Drawn inside, it survives.
          className="focus-visible:inset-ring-ring shrink-0 opacity-50 hover:opacity-100 focus-visible:inset-ring-2 focus-visible:ring-0 focus-visible:outline-none"
        >
          {collapsed ? <PanelLeftOpenIcon /> : <PanelLeftCloseIcon />}
        </Button>
        {!collapsed && (
          <p className="text-muted-foreground text-xs font-medium tracking-wide whitespace-nowrap uppercase">
            Add a step
          </p>
        )}
      </div>

      <ul
        id="component-palette-items"
        className={cn('flex flex-col', collapsed ? 'gap-1' : 'gap-1.5')}
      >
        {PALETTE_ITEMS.map(({ type, label, description, Icon }) => (
          <li key={type}>
            <button
              type="button"
              draggable
              onDragStart={(event) => {
                event.dataTransfer.setData(PALETTE_DRAG_TYPE, type);
                // `move` would suggest the palette item is being taken away.
                event.dataTransfer.effectAllowed = 'copy';
              }}
              onClick={() => onAdd(type)}
              // The accessible name is the same either way, so the control a
              // screen reader or a test finds does not change when the panel
              // folds — only what a sighted user reads does.
              aria-label={`Add ${label}`}
              // Collapsed, the label has to arrive some other way: a native
              // tooltip is the one that needs no hover target of its own.
              title={collapsed ? `Add ${label}` : undefined}
              className={cn(
                // Inset for the same reason as the toggle above: collapsed,
                // this control fills the rail exactly; expanded, it fills the
                // panel exactly. Either way an outward ring falls outside the
                // clip and is never seen.
                'border-border hover:bg-accent focus-visible:inset-ring-ring flex border transition-colors',
                'cursor-grab active:cursor-grabbing focus-visible:inset-ring-2 focus-visible:outline-none',
                collapsed
                  ? // `size-7` matches the toggle's `icon-sm` exactly, so the rail
                    // is one column of identical targets rather than a stack of
                    // differently-shaped ones.
                    'size-7 items-center justify-center rounded-[min(var(--radius-md),12px)]'
                  : 'w-full items-start gap-2.5 rounded-lg p-2.5 text-left',
              )}
            >
              <Icon
                aria-hidden="true"
                className={cn(
                  'text-muted-foreground size-4 shrink-0',
                  !collapsed && 'mt-0.5',
                )}
                strokeWidth={1.5}
              />
              {!collapsed && (
                <span className="flex min-w-0 flex-col">
                  <span className="text-foreground text-sm font-medium">
                    {label}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {description}
                  </span>
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
